'use client';

import SliderWithDescription from './SliderWithDescription';
import { BookOpen, Users, Globe, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';

interface StoryFoundationSectionProps {
  values: {
    plot_focus: number;
    character_focus: number;
    worldbuilding_depth: number;
    theme_focus: number;
  };
  onChange: (key: string, value: number) => void;
  stepBadgeLabel?: string;
  stepBadgeClassName?: string;
  showHeader?: boolean;
}

export default function StoryFoundationSection({
  values,
  onChange,
  stepBadgeLabel,
  stepBadgeClassName,
  showHeader = true
}: StoryFoundationSectionProps) {
  return (
    <AppErrorBoundary source="story-foundation-section">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <div className="space-y-8">
        {showHeader && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              {stepBadgeLabel && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide backdrop-blur-md transition-colors',
                    stepBadgeClassName
                  )}
                >
                  {stepBadgeLabel}
                </span>
              )}
              <h3 className={cn('flex items-center gap-2 text-xl font-bold text-theme-text-primary')}>
                <Sparkles className="w-5 h-5 accent-text-primary" />
                Story Foundation
              </h3>
            </div>
            <p className={cn('text-sm text-theme-text-secondary')}>
              Tell us what kind of story you want to tell. These guide the AI to generate the perfect story bible.
            </p>
          </div>
        )}

        <SliderWithDescription
          value={values.plot_focus}
          onChange={(val) => onChange('plot_focus', val)}
          title="Plot Focus"
          description="How much does the story revolve around events and conflict?"
          descriptionKey="plot_focus"
          icon={<BookOpen className="w-5 h-5 text-theme-text-info" />}
          levels={[
            { value: 1, label: 'Minimal' },
            { value: 2, label: 'Light' },
            { value: 3, label: 'Balanced' },
            { value: 4, label: 'Heavy' },
            { value: 5, label: 'Intense' }
          ]}
        />

        <SliderWithDescription
          value={values.character_focus}
          onChange={(val) => onChange('character_focus', val)}
          title="Character Focus"
          description="How complex and central are your characters?"
          descriptionKey="character_focus"
          icon={<Users className="w-5 h-5 text-theme-text-success" />}
          levels={[
            { value: 1, label: 'Simple' },
            { value: 2, label: 'Basic' },
            { value: 3, label: 'Balanced' },
            { value: 4, label: 'Complex' },
            { value: 5, label: 'Deep' }
          ]}
        />

        <SliderWithDescription
          value={values.worldbuilding_depth}
          onChange={(val) => onChange('worldbuilding_depth', val)}
          title="Worldbuilding Depth"
          description="How much detail and lore should your world have?"
          descriptionKey="worldbuilding_depth"
          icon={<Globe className="w-5 h-5 text-theme-text-info" />}
          levels={[
            { value: 1, label: 'Minimal' },
            { value: 2, label: 'Light' },
            { value: 3, label: 'Balanced' },
            { value: 4, label: 'Extensive' },
            { value: 5, label: 'Epic' }
          ]}
        />

        <SliderWithDescription
          value={values.theme_focus}
          onChange={(val) => onChange('theme_focus', val)}
          title="Theme Focus"
          description="How philosophical should your story be?"
          descriptionKey="theme_focus"
          icon={<Sparkles className="w-5 h-5 accent-text-primary" />}
          levels={[
            { value: 1, label: 'Light' },
            { value: 2, label: 'Subtle' },
            { value: 3, label: 'Balanced' },
            { value: 4, label: 'Clear' },
            { value: 5, label: 'Deep' }
          ]}
        />
      </div>
    </AppErrorBoundary>
  );
}
